import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, ExternalLink, Trash2, Clock, Crown, Loader2, Settings, LogOut, ChevronDown, User, Mail, Check, MessageSquare, Calendar, Archive, Search } from 'lucide-react';
import axios from 'axios';
import { useToast, ToastContainer } from '../ui/Toast';
import EmbeddedCheckoutModal from '../ui/EmbeddedCheckout';
import { PostCheckoutSuccess, SubscriptionDisclosure } from '../ui/BillingComponents';
import SEOAudit from './SEOAudit';
import './builder-v2.css';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const PLANS = {
  launch_monthly: { name: 'Launch', price: '$19', interval: 'mo', yearly: false },
  launch_yearly: { name: 'Launch', price: '$16', interval: 'mo', yearly: true, total: '$192/yr' },
  pro_monthly: { name: 'Pro', price: '$39', interval: 'mo', yearly: false },
  pro_yearly: { name: 'Pro', price: '$33', interval: 'mo', yearly: true, total: '$396/yr' },
};

const Dashboard = ({ user }) => {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [showPricing, setShowPricing] = useState(false);
  const [yearly, setYearly] = useState(true);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const { toast: toastData, confirm: confirmData, showToast, showConfirm, handleConfirm, setToast } = useToast();
  const [checkingOut, setCheckingOut] = useState(false);
  const [pollingSession, setPollingSession] = useState(null);
  const [limits, setLimits] = useState(null);
  const [checkoutPlanKey, setCheckoutPlanKey] = useState(null);
  const [showPostCheckout, setShowPostCheckout] = useState(null); // {plan, planKey}
  const [leads, setLeads] = useState([]);
  const [leadStats, setLeadStats] = useState(null);
  const [leadFilter, setLeadFilter] = useState(null);
  const [digest, setDigest] = useState(null);
  const [siteAnalytics, setSiteAnalytics] = useState({});
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (!user) { navigate('/auth'); return; }
    fetchSites();
    fetchSubscription();
    fetchLimits();
    fetchLeads();
    fetchLeadStats();
    fetchDigest();
    // Check if returning from Stripe
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSites = async () => {
    try {
      const res = await axios.get(`${API}/sites`, { withCredentials: true });
      setSites(res.data.sites);
      // Fetch analytics for published sites
      res.data.sites.forEach(s => { if (s.published?.status === 'live') fetchSiteViews(s.id); });
    } catch {
      setSites([]);
      navigate('/auth');
    }
    setLoading(false);
  };

  const fetchSubscription = async () => {
    try {
      const res = await axios.get(`${API}/billing/subscription`, { withCredentials: true });
      setSubscription(res.data);
    } catch { /* ignore */ }
  };

  const fetchLimits = async () => {
    try {
      const res = await axios.get(`${API}/billing/limits`, { withCredentials: true });
      setLimits(res.data);
    } catch { /* ignore */ }
  };

  const fetchLeads = async (filter = null) => {
    try {
      const params = filter ? `?status=${filter}` : '';
      const res = await axios.get(`${API}/leads${params}`, { withCredentials: true });
      setLeads(res.data.leads || []);
    } catch { setLeads([]); }
  };

  const fetchLeadStats = async () => {
    try {
      const res = await axios.get(`${API}/leads/stats`, { withCredentials: true });
      setLeadStats(res.data);
    } catch { /* ignore */ }
  };

  const fetchDigest = async () => {
    try {
      const res = await axios.get(`${API}/digest`, { withCredentials: true });
      setDigest(res.data);
    } catch { /* ignore */ }
  };

  const fetchSiteViews = async (siteId) => {
    if (siteAnalytics[siteId]) return;
    try {
      const res = await axios.get(`${API}/sites/${siteId}/views?days=30`, { withCredentials: true });
      setSiteAnalytics(prev => ({ ...prev, [siteId]: res.data }));
    } catch { /* ignore */ }
  };

  const updateLeadStatus = async (leadId, status) => {
    try {
      await axios.patch(`${API}/leads/${leadId}`, { status }, { withCredentials: true });
      setLeads(prev => prev.map(l => l.id === leadId ? { ...l, status } : l));
      fetchLeadStats();
    } catch { /* ignore */ }
  };

  const pollPaymentStatus = useCallback(async (sessionId, attempt = 0) => {
    // FIX #12: Timeout after 24 attempts (~60 seconds)
    if (attempt >= 24) {
      setPollingSession(null);
      showToast('Payment received! Refresh the page if your plan hasn\'t updated yet.');
      window.history.replaceState({}, '', '/dashboard');
      return;
    }
    setPollingSession(sessionId);
    try {
      const res = await axios.get(`${API}/billing/status/${sessionId}`, { withCredentials: true });
      if (res.data.payment_status === 'paid') {
        setPollingSession(null);
        await fetchSubscription();
        await fetchLimits();
        window.history.replaceState({}, '', '/dashboard');
        // FIX #3/#21: Show branded success screen
        setShowPostCheckout({ plan: res.data.plan, planKey: res.data.plan_key || searchParams.get('plan') });
        return;
      }
    } catch { /* retry */ }
    setTimeout(() => pollPaymentStatus(sessionId, attempt + 1), 2500);
  }, []);

  const handleCheckout = async (planKey) => {
    setCheckoutPlanKey(planKey);
    setShowPricing(false);
  };

  const handleCheckoutSuccess = async () => {
    setCheckoutPlanKey(null);
    await fetchSubscription();
    await fetchLimits();
  };

  const [showBillingModal, setShowBillingModal] = useState(false);
  const [seoSiteId, setSeoSiteId] = useState(null);
  const [seoSiteName, setSeoSiteName] = useState('');

  const handleManageBilling = () => {
    setShowBillingModal(true);
  };

  const deleteSite = async (siteId) => {
    const ok = await showConfirm('Delete this site and all its pages? This can\'t be undone.');
    if (!ok) return;
    try {
      await axios.delete(`${API}/sites/${siteId}`, { withCredentials: true });
      setSites(sites.filter(s => s.id !== siteId));
    } catch {
      showToast('Couldn\'t delete that site. Try again.', 'error');
    }
  };

  const handleLogout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    navigate('/');
  };

  const formatDate = (iso) => new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const isSubscribed = (subscription && subscription.plan !== 'free' && subscription.status === 'active') || (limits && limits.plan !== 'free');
  const planLabel = limits?.plan === 'pro' ? 'Pro' : limits?.plan === 'launch' ? 'Launch' : (subscription?.plan === 'pro' ? 'Pro' : subscription?.plan === 'launch' ? 'Launch' : null);

  if (loading) return (
    <div className="app-page" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#faf8f5' }}>
      <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: 'rgba(201,168,76,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px', animation: 'pulse 2s ease-in-out infinite' }}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>
      </div>
      <p style={{ fontFamily: 'var(--serif)', fontSize: '20px', color: '#1F1D1B', marginBottom: '6px', letterSpacing: '-.02em' }}>golden<em style={{ fontStyle: 'italic', color: '#C9A84C' }}>hour</em></p>
      <p style={{ fontSize: '13px', color: '#8c8782', fontFamily: 'var(--sans)' }}>Loading your dashboard...</p>
      <style>{`@keyframes pulse{0%,100%{opacity:0.4;transform:scale(0.95)}50%{opacity:1;transform:scale(1.05)}}`}</style>
    </div>
  );

  if (!sites) return (
    <div className="app-page" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#faf8f5' }}>
      <p style={{ fontFamily: 'var(--serif)', fontSize: '20px', color: '#1F1D1B', marginBottom: '12px', letterSpacing: '-.02em' }}>golden<em style={{ fontStyle: 'italic', color: '#C9A84C' }}>hour</em></p>
      <p style={{ fontSize: '14px', color: '#8c8782', fontFamily: 'var(--sans)', marginBottom: '20px' }}>Something went wrong loading your dashboard.</p>
      <button onClick={() => window.location.reload()} style={{ padding: '10px 24px', background: '#C9A84C', color: '#fff', border: 'none', borderRadius: '8px', fontFamily: 'var(--sans)', fontSize: '14px', cursor: 'pointer' }}>Refresh Page</button>
    </div>
  );

  // FIX #3/#21: Post-checkout success celebration
  if (showPostCheckout) {
    return (
      <PostCheckoutSuccess
        plan={showPostCheckout.plan}
        planKey={showPostCheckout.planKey}
        onContinue={() => { setShowPostCheckout(null); fetchSites(); fetchLimits(); }}
      />
    );
  }

  return (
    <div className="builder-v2" style={{ minHeight: '100vh', background: '#faf8f5', color: '#2a2725' }}>
      <ToastContainer toast={toastData} confirm={confirmData} onConfirm={handleConfirm} onDismiss={() => setToast(null)} />
      {/* Brand Bar */}
      <div data-testid="brand-bar" style={{ height: '32px', background: '#0a0907', borderBottom: '1px solid rgba(201,168,76,0.04)', position: 'sticky', top: 0, zIndex: 60, display: 'flex', alignItems: 'center', paddingLeft: '8px' }}>
        <a href="https://uncutgems.com" target="_blank" rel="noopener noreferrer" style={{ color: 'rgba(255,255,255,0.6)', fontSize: '9px', letterSpacing: '0.15em', textTransform: 'uppercase', textDecoration: 'none', padding: '0 12px', borderRight: '1px solid rgba(255,255,255,0.08)', lineHeight: '32px' }}>Uncut Gems</a>
        <a href="https://roughcut.com/" target="_blank" rel="noopener noreferrer" style={{ color: 'rgba(255,255,255,0.6)', fontSize: '9px', letterSpacing: '0.15em', textTransform: 'uppercase', textDecoration: 'none', padding: '0 12px', borderRight: '1px solid rgba(255,255,255,0.08)', lineHeight: '32px' }}>RoughCut</a>
        <a href="https://pixally.com" target="_blank" rel="noopener noreferrer" style={{ color: 'rgba(255,255,255,0.6)', fontSize: '9px', letterSpacing: '0.15em', textTransform: 'uppercase', textDecoration: 'none', padding: '0 12px', borderRight: '1px solid rgba(255,255,255,0.08)', lineHeight: '32px' }}>Pixally</a>
        <span style={{ color: '#C9A84C', fontSize: '9px', letterSpacing: '0.15em', textTransform: 'uppercase', fontWeight: 'bold', padding: '0 12px', borderRight: '1px solid rgba(255,255,255,0.08)', lineHeight: '32px' }}>GoldenHour</span>
      </div>
      <nav style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 5vw', borderBottom: '1px solid #e8e3dc', background: '#fff' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
          <a href="/" style={{ fontFamily: 'var(--serif)', fontSize: '20px', color: '#1F1D1B', textDecoration: 'none', letterSpacing: '-.02em' }}>
            golden<em style={{ fontStyle: 'italic', color: '#C9A84C' }}>hour</em>
          </a>
          <span style={{ fontFamily: 'var(--mono)', fontSize: '8px', letterSpacing: '.12em', textTransform: 'uppercase', color: 'rgba(42,39,37,.35)' }}>by Uncut Gems</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {isSubscribed && (
            <button onClick={handleManageBilling} style={{ fontFamily: 'var(--sans)', fontSize: '9px', letterSpacing: '.1em', textTransform: 'uppercase', color: '#C9A84C', background: 'rgba(196,162,101,0.06)', border: '1px solid rgba(196,162,101,0.12)', padding: '5px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }} data-testid="manage-billing-btn">
              <Crown size={10} /> {planLabel} Plan
            </button>
          )}
          {!isSubscribed && (
            <button onClick={() => setShowPricing(true)} className="btn-primary" style={{ padding: '8px 20px', fontSize: '10px' }} data-testid="subscribe-btn">
              Subscribe
            </button>
          )}
          <div style={{ position: 'relative' }}>
            <button onClick={() => setShowUserMenu(!showUserMenu)} data-testid="user-menu-trigger" style={{
              display: 'flex', alignItems: 'center', gap: '8px', background: '#fff', border: '1px solid #e8e3dc',
              padding: '6px 12px', cursor: 'pointer', color: '#2a2725', fontFamily: 'var(--sans)', fontSize: '13px', borderRadius: '10px',
            }}>
              <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: '#C9A84C', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ color: '#fff', fontSize: '11px', fontWeight: 700 }}>{(user?.name || user?.email || '?')[0].toUpperCase()}</span>
              </div>
              <span style={{ maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.name || user?.email}</span>
              <ChevronDown size={12} style={{ color: '#8c8782' }} />
            </button>

            {showUserMenu && (
              <>
                <div onClick={() => setShowUserMenu(false)} style={{ position: 'fixed', inset: 0, zIndex: 49 }} />
                <div data-testid="user-menu-dropdown" style={{
                  position: 'absolute', right: 0, top: '100%', marginTop: '6px', width: '220px',
                  background: '#fff', border: '1px solid #e8e3dc', zIndex: 50, borderRadius: '12px',
                  boxShadow: '0 8px 30px rgba(0,0,0,0.1)',
                }}>
                  <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0ece6' }}>
                    <p style={{ fontSize: '13px', color: '#2a2725', fontWeight: 600, fontFamily: 'var(--sans)' }}>{user?.name || 'User'}</p>
                    <p style={{ fontSize: '11px', color: '#8c8782', marginTop: '2px', fontFamily: 'var(--sans)' }}>{user?.email}</p>
                  </div>
                  <div style={{ padding: '4px 0' }}>
                    <a href="/settings" onClick={e => { e.preventDefault(); navigate('/settings'); setShowUserMenu(false); }} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 16px', fontSize: '13px', color: '#2a2725', textDecoration: 'none', transition: 'background .15s', fontFamily: 'var(--sans)' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f5f2ee'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <Settings size={14} style={{ color: '#8c8782' }} /> Settings
                    </a>
                    {isSubscribed && (
                      <button onClick={() => { handleManageBilling(); setShowUserMenu(false); }} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 16px', fontSize: '13px', color: '#2a2725', background: 'none', border: 'none', cursor: 'pointer', width: '100%', textAlign: 'left', transition: 'background .15s', fontFamily: 'var(--sans)' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f5f2ee'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <Crown size={14} style={{ color: '#C9A84C' }} /> Manage Plan
                      </button>
                    )}
                  </div>
                  <div style={{ borderTop: '1px solid #f0ece6', padding: '4px 0' }}>
                    <a href="/help" onClick={e => { e.preventDefault(); navigate('/help'); setShowUserMenu(false); }} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 16px', fontSize: '13px', color: '#2a2725', textDecoration: 'none', transition: 'background .15s', fontFamily: 'var(--sans)' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f5f2ee'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <MessageSquare size={14} style={{ color: '#8c8782' }} /> Help Center
                    </a>
                    <button onClick={() => { handleLogout(); setShowUserMenu(false); }} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 16px', fontSize: '13px', color: '#c47272', background: 'none', border: 'none', cursor: 'pointer', width: '100%', textAlign: 'left', transition: 'background .15s' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(196,71,114,0.06)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <LogOut size={14} /> Sign Out
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Payment processing banner */}
      {pollingSession && (
        <div style={{ padding: '12px 20px', background: 'rgba(196,162,101,0.06)', borderBottom: '1px solid rgba(196,162,101,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
          <Loader2 size={14} className="animate-spin" style={{ color: '#C9A84C' }} />
          <span style={{ fontFamily: 'var(--sans)', fontSize: '13px', color: '#2a2725' }}>Activating your subscription...</span>
        </div>
      )}

      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '60px 5vw' }}>
        {/* ── Quick Stats ── */}
        {/* ── Email Verification Banner ── */}
        {user && user.email_verified === false && (
          <div data-testid="verify-email-banner" style={{
            padding: '14px 20px', marginBottom: '24px', borderRadius: '10px',
            background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.12)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px',
          }}>
            <p style={{ fontFamily: 'var(--sans)', fontSize: '13px', color: '#8c8782' }}>
              Please verify your email address. Check your inbox for the verification link.
            </p>
            <button onClick={async () => {
              try {
                await axios.post(`${API}/auth/resend-verification`, {}, { withCredentials: true });
                showToast('Verification email sent!');
              } catch { showToast('Could not send verification email.', 'error'); }
            }} style={{
              padding: '8px 16px', fontFamily: 'var(--sans)', fontSize: '12px', fontWeight: 500,
              background: '#fff', color: '#C9A84C', border: '1px solid rgba(201,168,76,0.2)',
              borderRadius: '8px', cursor: 'pointer', whiteSpace: 'nowrap',
            }}>
              Resend Email
            </button>
          </div>
        )}

        {leadStats && leadStats.total > 0 && (
          <div data-testid="quick-stats" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '40px' }}>
            {[
              { label: 'Views This Month', value: '—', id: 'stat-views' },
              { label: 'Inquiries This Month', value: leadStats.this_month || 0, id: 'stat-inquiries' },
              { label: 'Booking Rate', value: `${leadStats.booking_rate || 0}%`, id: 'stat-booking' },
            ].map(stat => (
              <div key={stat.id} data-testid={stat.id} style={{ padding: '24px', border: '1px solid #e8e3dc', textAlign: 'center' }}>
                <p style={{ fontFamily: 'var(--sans)', fontSize: '32px', color: '#2a2725', marginBottom: '4px' }}>{stat.value}</p>
                <p style={{ fontFamily: 'var(--sans)', fontSize: '9px', color: '#8c8782', letterSpacing: '.1em', textTransform: 'uppercase' }}>{stat.label}</p>
              </div>
            ))}
          </div>
        )}

        {/* ── Past Due Banner (FIX #5) ── */}
        {subscription && subscription.status === 'past_due' && (
          <div data-testid="past-due-banner" style={{
            padding: '20px 24px', marginBottom: '32px', borderRadius: '12px',
            background: 'rgba(196,72,72,0.06)', border: '1px solid rgba(196,72,72,0.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px',
          }}>
            <div>
              <p style={{ fontFamily: 'var(--sans)', fontSize: '15px', fontWeight: 600, color: '#c44848', marginBottom: '4px' }}>
                Payment failed
              </p>
              <p style={{ fontFamily: 'var(--sans)', fontSize: '13px', color: '#8c8782', lineHeight: 1.6 }}>
                Your last payment didn't go through. Update your payment method to keep your site online and continue editing.
              </p>
            </div>
            <button onClick={async () => {
              try {
                const res = await axios.post(`${API}/billing/portal`, { plan_key: '', origin_url: window.location.origin }, { withCredentials: true });
                if (res.data.url) window.location.href = res.data.url;
              } catch {}
            }} data-testid="update-payment-btn" style={{
              padding: '12px 28px', fontFamily: 'var(--sans)', fontSize: '13px', fontWeight: 600,
              background: '#c44848', color: '#fff', border: 'none', borderRadius: '10px',
              cursor: 'pointer', whiteSpace: 'nowrap',
            }}>
              Update Payment Method
            </button>
          </div>
        )}

        {/* ── Cancellation Pending Banner (FIX #18) ── */}
        {subscription && subscription.cancel_at_period_end && subscription.status !== 'canceled' && (
          <div data-testid="canceling-banner" style={{
            padding: '20px 24px', marginBottom: '32px', borderRadius: '12px',
            background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.12)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px',
          }}>
            <div>
              <p style={{ fontFamily: 'var(--sans)', fontSize: '15px', fontWeight: 600, color: '#2a2725', marginBottom: '4px' }}>
                Your plan has been canceled
              </p>
              <p style={{ fontFamily: 'var(--sans)', fontSize: '13px', color: '#8c8782', lineHeight: 1.6 }}>
                Access continues until {subscription.cancel_at ? new Date(subscription.cancel_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : 'the end of your billing period'}. Your sites will go offline after that.
              </p>
            </div>
          </div>
        )}

        {/* ── Cancelled Subscription Banner ── */}
        {subscription && subscription.status === 'canceled' && (
          <div data-testid="canceled-banner" style={{
            padding: '20px 24px', marginBottom: '32px', borderRadius: '12px',
            background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px',
          }}>
            <div>
              <p style={{ fontFamily: 'var(--sans)', fontSize: '15px', fontWeight: 600, color: '#2a2725', marginBottom: '4px' }}>
                Your subscription has ended
              </p>
              <p style={{ fontFamily: 'var(--sans)', fontSize: '13px', color: '#8c8782', lineHeight: 1.6 }}>
                Your sites are saved and waiting. Resubscribe to bring them back online and keep editing with Iris.
              </p>
            </div>
            <button onClick={() => setShowPricing(true)} data-testid="resubscribe-btn" style={{
              padding: '12px 28px', fontFamily: 'var(--sans)', fontSize: '13px', fontWeight: 600,
              background: '#C9A84C', color: '#fff', border: 'none', borderRadius: '10px',
              cursor: 'pointer', whiteSpace: 'nowrap', boxShadow: '0 2px 8px rgba(201,168,76,0.2)',
            }}>
              Resubscribe
            </button>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '60px' }}>
          <div>
            <h1 style={{ fontFamily: 'var(--sans)', fontSize: '40px', marginBottom: '8px' }}>Your Sites</h1>
            <p style={{ color: '#8c8782', fontSize: '14px' }}>
              {sites.length === 0 ? 'Your first site is just 90 seconds away.' : (
                <>
                  {sites.length} site{sites.length > 1 ? 's' : ''}
                  {limits && <span style={{ fontFamily: 'var(--sans)', fontSize: '10px', marginLeft: '8px', color: limits.sites.can_create ? '#8c8782' : '#C9A84C' }}>
                    ({limits.sites.used}/{limits.sites.limit} on {limits.plan === 'pro' ? 'Pro' : limits.plan === 'launch' ? 'Launch' : 'Free'})
                  </span>}
                </>
              )}
            </p>
          </div>
          <button onClick={() => {
            if (limits && !limits.sites.can_create) {
              if (limits.plan === 'pro') {
                showToast(`You've reached the limit of ${limits.sites.limit} sites on your Pro plan.`, 'info');
              } else {
                setShowPricing(true);
              }
              return;
            }
            navigate('/build');
          }} className="btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '8px' }} data-testid="dashboard-new-site">
            <Plus size={12} /> New Site
          </button>
        </div>

        {loading ? (
          <p style={{ textAlign: 'center', color: '#8c8782', padding: '80px 0' }}>Loading...</p>
        ) : sites.length === 0 ? (
          <div data-testid="dashboard-empty" style={{ textAlign: 'center', padding: '80px 24px', border: '1px solid #e8e3dc', borderRadius: '16px', background: '#fff' }}>
            <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: 'rgba(201,168,76,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
              <Sparkles size={28} style={{ color: '#C9A84C' }} />
            </div>
            <h3 style={{ fontFamily: 'var(--serif)', fontSize: '26px', marginBottom: '10px', color: '#1F1D1B', letterSpacing: '-.02em' }}>
              Let's build something <em style={{ fontStyle: 'italic', color: '#C9A84C' }}>beautiful.</em>
            </h3>
            <p style={{ color: '#8c8782', fontSize: '15px', lineHeight: 1.7, marginBottom: '8px', maxWidth: '380px', margin: '0 auto 8px', fontFamily: 'var(--sans)' }}>
              Describe a mood, a film, a feeling — and Iris will craft a one-of-a-kind website for your studio.
            </p>
            <p style={{ color: '#bfb9b3', fontSize: '12px', marginBottom: '32px', fontFamily: 'var(--sans)' }}>
              Takes about 3 minutes. Free to try.
            </p>
            <button onClick={() => navigate('/build')} className="btn-primary" data-testid="dashboard-build-first" style={{ padding: '16px 40px', fontSize: '14px' }}>Build Your First Site</button>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '20px' }}>
            {sites.map(site => {
              const homeHtml = site.pages?.find(p => p.is_home)?.html || site.pages?.[0]?.html || '';
              return (
              <div key={site.id} style={{ cursor: 'pointer', overflow: 'hidden', border: '1px solid #e8e3dc', borderRadius: '12px', background: '#fff', transition: 'all .2s', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
                onClick={() => navigate(`/builder/${site.id}`)} data-testid={`site-card-${site.id}`}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'; e.currentTarget.style.transform = 'none'; }}>
                {/* Live preview thumbnail */}
                <div style={{ height: '180px', overflow: 'hidden', position: 'relative', background: '#f0ece6', borderBottom: '1px solid #e8e3dc' }}>
                  {homeHtml ? (
                    <iframe
                      srcDoc={homeHtml}
                      title={site.business_name}
                      style={{ width: '250%', height: '250%', transform: 'scale(0.4)', transformOrigin: 'top left', border: 'none', pointerEvents: 'none' }}
                      loading="lazy"
                      tabIndex={-1}
                    />
                  ) : (
                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ fontFamily: 'var(--sans)', fontSize: '36px', color: '#C9A84C', opacity: 0.2 }}>{site.business_name?.charAt(0) || 'S'}</span>
                    </div>
                  )}
                </div>
                <div style={{ padding: '16px' }}>
                  <p style={{ fontFamily: 'var(--sans)', fontSize: '16px', fontWeight: 600, color: '#2a2725', marginBottom: '6px' }}>{site.business_name}</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px', flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: 'var(--sans)', fontSize: '11px', color: '#C9A84C', background: 'rgba(201,168,76,0.06)', padding: '3px 10px', borderRadius: '9999px', fontWeight: 500 }}>
                      {site.style}
                    </span>
                    {(site.page_count || 1) > 1 && (
                      <span style={{ fontFamily: 'var(--sans)', fontSize: '11px', color: '#8c8782', background: '#f5f2ee', padding: '3px 10px', borderRadius: '9999px' }}>
                        {site.page_count} pages
                      </span>
                    )}
                    {site.published?.status === 'live' && (
                      <span style={{ fontFamily: 'var(--sans)', fontSize: '11px', color: '#5ba89d', background: 'rgba(91,168,157,0.06)', padding: '3px 10px', borderRadius: '9999px', fontWeight: 500 }}>
                        Live
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontFamily: 'var(--sans)', fontSize: '11px', color: '#8c8782' }}>{formatDate(site.updated_at)}</span>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button onClick={e => { e.stopPropagation(); setSeoSiteId(site.id); setSeoSiteName(site.business_name); }} data-testid={`seo-btn-${site.id}`} style={{ background: 'none', border: '1px solid rgba(201,168,76,0.15)', cursor: 'pointer', color: '#C9A84C', padding: '4px 10px', display: 'flex', alignItems: 'center', gap: '4px', fontFamily: 'var(--sans)', fontSize: '11px', borderRadius: '8px', fontWeight: 500 }}>
                        <Search size={11} /> SEO
                      </button>
                      <button onClick={e => { e.stopPropagation(); navigate(`/builder/${site.id}`); }} style={{ background: 'none', border: '1px solid #e8e3dc', cursor: 'pointer', color: '#8c8782', padding: '4px 8px', borderRadius: '8px' }}>
                        <ExternalLink size={13} />
                      </button>
                      <button onClick={e => { e.stopPropagation(); deleteSite(site.id); }} style={{ background: 'none', border: '1px solid #e8e3dc', cursor: 'pointer', color: '#8c8782', padding: '4px 8px', borderRadius: '8px' }}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Share & Referral ── */}
      {isSubscribed && sites.length > 0 && sites.some(s => s.published?.status === 'live') && (
        <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 5vw 40px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            {/* Share Your Site */}
            <div data-testid="share-site-card" style={{ padding: '24px', border: '1px solid #e8e3dc', background: 'rgba(196,162,101,0.02)' }}>
              <p style={{ fontFamily: 'var(--sans)', fontSize: '9px', color: '#C9A84C', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: '8px' }}>Share Your Site</p>
              <p style={{ fontFamily: 'var(--sans)', fontSize: '13px', color: '#8c8782', marginBottom: '16px' }}>
                Show off your new site to the world.
              </p>
              {sites.filter(s => s.published?.status === 'live').map(site => (
                <div key={site.id} style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}>
                  <input readOnly value={`https://${site.published?.subdomain}.goldenhour.build`} style={{ flex: 1, padding: '6px 10px', fontSize: '11px', fontFamily: 'var(--sans)', background: 'rgba(242,239,233,0.04)', border: '1px solid #e8e3dc', color: '#2a2725', outline: 'none' }} />
                  <button onClick={() => { navigator.clipboard.writeText(`https://${site.published?.subdomain}.goldenhour.build`); showToast('Link copied!', 'success'); }} style={{ padding: '6px 12px', fontFamily: 'var(--sans)', fontSize: '9px', background: '#C9A84C', color: '#000', border: 'none', cursor: 'pointer', letterSpacing: '.06em' }}>
                    COPY
                  </button>
                </div>
              ))}
            </div>

            {/* Referral Program */}
            <div data-testid="referral-card" style={{ padding: '24px', border: '1px solid #e8e3dc' }}>
              <p style={{ fontFamily: 'var(--sans)', fontSize: '9px', color: '#C9A84C', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: '8px' }}>Refer a Photographer</p>
              <p style={{ fontFamily: 'var(--sans)', fontSize: '13px', color: '#8c8782', marginBottom: '16px' }}>
                Share your link. When they subscribe, you both get a free month.
              </p>
              <div style={{ display: 'flex', gap: '6px' }}>
                <input readOnly value={`goldenhour.build?ref=${user?.id?.substring(0, 8) || ''}`} style={{ flex: 1, padding: '6px 10px', fontSize: '11px', fontFamily: 'var(--sans)', background: 'rgba(242,239,233,0.04)', border: '1px solid #e8e3dc', color: '#2a2725', outline: 'none' }} />
                <button onClick={() => { navigator.clipboard.writeText(`https://goldenhour.build?ref=${user?.id?.substring(0, 8) || ''}`); showToast('Referral link copied!', 'success'); }} style={{ padding: '6px 12px', fontFamily: 'var(--sans)', fontSize: '9px', background: '#C9A84C', color: '#000', border: 'none', cursor: 'pointer', letterSpacing: '.06em' }}>
                  COPY
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* ── Site Analytics ── */}
      {sites.some(s => s.published?.status === 'live') && (
        <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 5vw 60px' }} data-testid="analytics-section">
          <h2 style={{ fontFamily: 'var(--sans)', fontSize: '28px', marginBottom: '4px' }}>Analytics</h2>
          <p style={{ color: '#8c8782', fontSize: '13px', marginBottom: '24px' }}>
            {digest ? `${digest.period} — your sites at a glance` : 'How your published sites are performing'}
          </p>

          {/* Digest Cards */}
          {digest && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginBottom: '32px' }}>
              {[
                { label: 'Views this month', value: digest.views_this_month, testid: 'stat-views' },
                { label: 'Inquiries', value: digest.inquiries_this_month, testid: 'stat-inquiries' },
                { label: 'Booking rate', value: `${digest.booking_rate}%`, testid: 'stat-booking' },
                { label: 'Total inquiries', value: digest.total_inquiries, testid: 'stat-total' },
              ].map(s => (
                <div key={s.label} data-testid={s.testid} style={{ padding: '20px', border: '1px solid #e8e3dc', background: 'rgba(242,239,233,0.02)' }}>
                  <p style={{ fontFamily: 'var(--sans)', fontSize: '9px', letterSpacing: '.12em', textTransform: 'uppercase', color: '#8c8782', marginBottom: '8px' }}>{s.label}</p>
                  <p style={{ fontFamily: 'var(--sans)', fontSize: '32px' }}>{s.value}</p>
                </div>
              ))}
            </div>
          )}

          {/* Per-site analytics */}
          {sites.filter(s => s.published?.status === 'live').map(site => {
            const data = siteAnalytics[site.id];
            if (!data) return null;
            const maxDay = Math.max(...(data.by_day?.map(d => d.views) || [1]), 1);
            return (
              <div key={site.id} style={{ marginBottom: '32px', border: '1px solid #e8e3dc', padding: '24px', background: 'rgba(242,239,233,0.02)' }} data-testid={`site-analytics-${site.id}`}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <div>
                    <p style={{ fontFamily: 'var(--sans)', fontSize: '18px' }}>{site.business_name}</p>
                    <p style={{ fontFamily: 'var(--sans)', fontSize: '9px', color: '#8c8782' }}>{data.total_views} views in the last {data.days} days</p>
                  </div>
                </div>

                {/* Views by day chart */}
                {data.by_day?.length > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    <p style={{ fontFamily: 'var(--sans)', fontSize: '9px', color: '#8c8782', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: '10px' }}>Daily Views</p>
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: '60px' }}>
                      {data.by_day.map((d, i) => (
                        <div key={i} title={`${d.date}: ${d.views} views`} style={{ flex: 1, minWidth: '4px', background: '#C9A84C', opacity: 0.7, height: `${Math.max((d.views / maxDay) * 100, 4)}%`, borderRadius: '1px 1px 0 0', transition: 'opacity .2s' }}
                          onMouseEnter={e => e.currentTarget.style.opacity = '1'}
                          onMouseLeave={e => e.currentTarget.style.opacity = '0.7'}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Referrers + Devices */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  {data.by_referrer?.length > 0 && (
                    <div>
                      <p style={{ fontFamily: 'var(--sans)', fontSize: '9px', color: '#8c8782', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: '8px' }}>Top Referrers</p>
                      {data.by_referrer.slice(0, 5).map((r, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #e8e3dc' }}>
                          <span style={{ fontSize: '12px', color: '#2a2725', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '200px' }}>{r.source}</span>
                          <span style={{ fontFamily: 'var(--sans)', fontSize: '11px', color: '#8c8782' }}>{r.views}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {data.by_device?.length > 0 && (
                    <div>
                      <p style={{ fontFamily: 'var(--sans)', fontSize: '9px', color: '#8c8782', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: '8px' }}>Devices</p>
                      {data.by_device.map((d, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #e8e3dc' }}>
                          <span style={{ fontSize: '12px', color: '#2a2725', textTransform: 'capitalize' }}>{d.device}</span>
                          <span style={{ fontFamily: 'var(--sans)', fontSize: '11px', color: '#8c8782' }}>{d.views}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Empty state */}
                {data.total_views === 0 && (
                  <p style={{ color: '#8c8782', fontSize: '13px', fontStyle: 'italic', textAlign: 'center', padding: '20px 0' }}>
                    No visitors yet. Share your site link to start getting traffic!
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
      {/* ── Lead Inbox ── */}
      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 5vw 60px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '24px' }}>
          <div>
            <h2 data-testid="leads-heading" style={{ fontFamily: 'var(--sans)', fontSize: '28px', marginBottom: '4px' }}>
              Lead Inbox
              {leadStats?.unread > 0 && (
                <span data-testid="leads-unread-badge" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '22px', height: '22px', borderRadius: '50%', background: '#C9A84C', color: '#000', fontSize: '11px', fontWeight: 700, fontFamily: 'var(--sans)', marginLeft: '10px', verticalAlign: 'middle' }}>
                  {leadStats.unread}
                </span>
              )}
            </h2>
            <p style={{ color: '#8c8782', fontSize: '13px' }}>
              {leadStats?.total ? `${leadStats.total} inquir${leadStats.total === 1 ? 'y' : 'ies'} from your published sites` : 'Inquiries from your published sites will appear here'}
            </p>
          </div>
          {/* Status filter pills + CSV export */}
          {leadStats?.total > 0 && (
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              {[
                { key: null, label: 'All' },
                { key: 'new', label: 'New' },
                { key: 'replied', label: 'Replied' },
                { key: 'booked', label: 'Booked' },
                { key: 'archived', label: 'Archived' },
              ].map(f => (
                <button
                  key={f.key || 'all'}
                  onClick={() => { setLeadFilter(f.key); fetchLeads(f.key); }}
                  data-testid={`lead-filter-${f.key || 'all'}`}
                  style={{
                    padding: '4px 12px', fontFamily: 'var(--sans)', fontSize: '9px', letterSpacing: '.06em',
                    textTransform: 'uppercase', border: '1px solid', cursor: 'pointer',
                    background: leadFilter === f.key ? 'rgba(196,162,101,0.08)' : 'transparent',
                    borderColor: leadFilter === f.key ? '#C9A84C' : '#e8e3dc',
                    color: leadFilter === f.key ? '#C9A84C' : '#8c8782',
                  }}
                >
                  {f.label}
                </button>
              ))}
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/leads/export`} data-testid="leads-csv-export" style={{
                padding: '4px 12px', fontFamily: 'var(--sans)', fontSize: '9px', letterSpacing: '.06em',
                textTransform: 'uppercase', border: '1px solid #e8e3dc', cursor: 'pointer',
                color: '#8c8782', textDecoration: 'none', display: 'inline-block',
              }}>CSV</a>
            </div>
          )}
        </div>

        {leads.length === 0 ? (
          <div data-testid="leads-empty" style={{ textAlign: 'center', padding: '60px 20px', border: '1px dashed #e8e3dc' }}>
            <Mail size={28} style={{ color: '#8c8782', margin: '0 auto 12px', opacity: 0.4 }} />
            <p style={{ fontFamily: 'var(--sans)', fontSize: '18px', marginBottom: '6px' }}>
              {leadFilter ? 'No leads with this status' : 'No inquiries yet'}
            </p>
            <p style={{ color: '#8c8782', fontSize: '13px' }}>
              {leadFilter ? 'Try a different filter.' : 'When visitors submit your contact form, their inquiries will appear here.'}
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {leads.map(lead => {
              const statusColors = {
                new: { bg: 'rgba(196,162,101,0.08)', border: 'rgba(196,162,101,0.2)', color: '#C9A84C' },
                read: { bg: 'rgba(242,239,233,0.04)', border: '#e8e3dc', color: '#8c8782' },
                replied: { bg: 'rgba(91,168,157,0.06)', border: 'rgba(91,168,157,0.2)', color: '#5ba89d' },
                booked: { bg: 'rgba(91,168,157,0.1)', border: 'rgba(91,168,157,0.3)', color: '#5ba89d' },
                archived: { bg: 'rgba(242,239,233,0.02)', border: '#e8e3dc', color: '#8c8782' },
              };
              const st = statusColors[lead.status] || statusColors.new;
              return (
                <div key={lead.id} data-testid={`lead-card-${lead.id}`} style={{
                  padding: '16px 20px', border: `1px solid ${st.border}`, background: st.bg,
                  display: 'flex', gap: '16px', alignItems: 'flex-start', transition: 'all .2s',
                }}>
                  {/* Status dot */}
                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: st.color, marginTop: '6px', flexShrink: 0 }} />

                  {/* Content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                      <span style={{ fontFamily: 'var(--sans)', fontSize: '14px', fontWeight: 600, color: '#2a2725' }}>
                        {lead.sender_name || 'Anonymous'}
                      </span>
                      <span style={{ fontFamily: 'var(--sans)', fontSize: '9px', color: '#8c8782', letterSpacing: '.05em' }}>
                        {lead.created_at ? new Date(lead.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
                      </span>
                    </div>
                    {lead.sender_email && (
                      <p style={{ fontSize: '12px', color: '#C9A84C', marginBottom: '4px' }}>
                        <a href={`mailto:${lead.sender_email}`} style={{ color: 'inherit', textDecoration: 'none' }}>{lead.sender_email}</a>
                      </p>
                    )}
                    {lead.message && (
                      <p style={{ fontSize: '13px', color: '#8c8782', lineHeight: 1.5, marginBottom: '4px', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                        {lead.message}
                      </p>
                    )}
                    {lead.date && (
                      <p style={{ fontSize: '11px', color: '#8c8782', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Calendar size={10} /> {lead.date}
                      </p>
                    )}
                    <p style={{ fontFamily: 'var(--sans)', fontSize: '8px', color: '#8c8782', letterSpacing: '.08em', textTransform: 'uppercase', marginTop: '4px' }}>
                      via {lead.subdomain}.goldenhour.build
                    </p>
                    {/* Notes field */}
                    <div style={{ marginTop: '8px' }}>
                      <input
                        type="text"
                        defaultValue={lead.notes || ''}
                        placeholder="Add a note..."
                        data-testid={`lead-notes-${lead.id}`}
                        onBlur={async (e) => {
                          const val = e.target.value.trim();
                          if (val !== (lead.notes || '')) {
                            try { await axios.patch(`${API}/leads/${lead.id}/notes`, { notes: val }, { withCredentials: true }); } catch {}
                          }
                        }}
                        style={{ width: '100%', padding: '4px 8px', fontSize: '11px', fontFamily: 'var(--sans)', background: 'rgba(242,239,233,0.03)', border: '1px solid #e8e3dc', color: '#2a2725', outline: 'none' }}
                      />
                    </div>
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flexShrink: 0 }}>
                    {/* Quick Reply — opens email client */}
                    {lead.sender_email && (
                      <a href={`mailto:${lead.sender_email}?subject=Re: Your inquiry${lead.date ? ' for ' + lead.date : ''}&body=Hi ${lead.sender_name || ''},%0A%0AThank you for reaching out!%0A%0A`}
                        title="Reply via email" data-testid={`lead-quickreply-${lead.id}`}
                        onClick={() => { if (lead.status === 'new') updateLeadStatus(lead.id, 'replied'); }}
                        style={{ width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #C9A84C', background: 'rgba(196,162,101,0.06)', cursor: 'pointer', color: '#C9A84C', textDecoration: 'none', transition: 'all .2s' }}>
                        <Mail size={12} />
                      </a>
                    )}
                    {lead.status !== 'booked' && (
                      <button onClick={() => updateLeadStatus(lead.id, 'booked')} title="Mark as booked" data-testid={`lead-book-${lead.id}`}
                        style={{ width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #e8e3dc', background: 'transparent', cursor: 'pointer', color: '#8c8782', transition: 'all .2s' }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = '#5ba89d'; e.currentTarget.style.color = '#5ba89d'; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = '#e8e3dc'; e.currentTarget.style.color = '#8c8782'; }}>
                        <Check size={12} />
                      </button>
                    )}
                    {lead.status !== 'archived' && (
                      <button onClick={() => updateLeadStatus(lead.id, 'archived')} title="Archive" data-testid={`lead-archive-${lead.id}`}
                        style={{ width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #e8e3dc', background: 'transparent', cursor: 'pointer', color: '#8c8782', transition: 'all .2s' }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = '#8c8782'; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = '#e8e3dc'; }}>
                        <Archive size={12} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Embedded Checkout */}
      {checkoutPlanKey && (
        <EmbeddedCheckoutModal
          planKey={checkoutPlanKey}
          returnPath="/dashboard"
          onClose={() => setCheckoutPlanKey(null)}
          onSuccess={handleCheckoutSuccess}
        />
      )}


      {/* Billing Management Modal */}
      {showBillingModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }} onClick={() => setShowBillingModal(false)}>
          <div onClick={e => e.stopPropagation()} data-testid="billing-modal" style={{ background: '#fff', border: '1px solid #e8e3dc', maxWidth: '640px', width: '95%', padding: '36px', borderRadius: '16px', boxShadow: '0 8px 40px rgba(0,0,0,0.1)', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ fontFamily: 'var(--sans)', fontSize: '22px', fontWeight: 600, color: '#2a2725' }}>Your Plan</h2>
              <button onClick={() => setShowBillingModal(false)} style={{ width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f2ee', border: 'none', borderRadius: '8px', cursor: 'pointer', color: '#8c8782', fontSize: '16px' }}>&times;</button>
            </div>

            {/* Plan comparison cards */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '24px' }}>
              {[
                { key: 'launch', name: 'Launch', price: '$19', annual: '$16', features: ['1 website', '30 AI edits / month', 'Free subdomain', 'Contact forms + lead inbox', 'Gallery embeds', 'Site analytics', 'SEO audit'] },
                { key: 'pro', name: 'Pro', price: '$39', annual: '$33', features: ['Up to 3 websites', 'Unlimited AI edits', 'Multi-page sites', 'Custom domain', 'AI blog writing', 'No GoldenHour branding', 'Priority support'] },
              ].map(plan => {
                const isCurrent = limits?.plan === plan.key;
                return (
                  <div key={plan.key} style={{
                    padding: '24px', borderRadius: '12px',
                    border: isCurrent ? '2px solid #C9A84C' : '1px solid #e8e3dc',
                    background: isCurrent ? 'rgba(201,168,76,0.02)' : '#faf8f5',
                    position: 'relative',
                  }}>
                    {isCurrent && (
                      <span style={{ position: 'absolute', top: '-10px', left: '16px', background: '#C9A84C', color: '#fff', fontSize: '10px', fontWeight: 600, padding: '2px 10px', borderRadius: '9999px', fontFamily: 'var(--sans)' }}>Current plan</span>
                    )}
                    <p style={{ fontFamily: 'var(--sans)', fontSize: '18px', fontWeight: 600, color: '#2a2725', marginBottom: '4px' }}>{plan.name}</p>
                    <div style={{ marginBottom: '16px' }}>
                      <span style={{ fontFamily: 'var(--sans)', fontSize: '32px', fontWeight: 700, color: '#2a2725' }}>{plan.price}</span>
                      <span style={{ fontFamily: 'var(--sans)', fontSize: '13px', color: '#8c8782' }}>/mo</span>
                      <p style={{ fontFamily: 'var(--sans)', fontSize: '11px', color: '#C9A84C', marginTop: '2px' }}>{plan.annual}/mo billed yearly</p>
                    </div>
                    <div style={{ borderTop: '1px solid #e8e3dc', paddingTop: '12px' }}>
                      {plan.features.map((f, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0' }}>
                          <div style={{ width: '16px', height: '16px', borderRadius: '50%', background: 'rgba(91,168,157,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <span style={{ color: '#5ba89d', fontSize: '10px', fontWeight: 700 }}>✓</span>
                          </div>
                          <span style={{ fontFamily: 'var(--sans)', fontSize: '13px', color: '#2a2725' }}>{f}</span>
                        </div>
                      ))}
                    </div>
                    {!isCurrent && (
                      <button onClick={() => {
                        setShowBillingModal(false);
                        const planKey = yearly ? `${plan.key}_yearly` : `${plan.key}_monthly`;
                        // If user already has active sub, use change-plan instead of new checkout
                        if (limits?.plan !== 'free' && limits?.subscription_status === 'active') {
                          axios.post(`${API}/billing/change-plan`, { plan_key: planKey }, { withCredentials: true })
                            .then(() => { fetchSubscription(); fetchLimits(); showToast(`Switched to ${plan.name}!`); })
                            .catch(err => showToast(err.response?.data?.detail || 'Could not change plan'));
                        } else {
                          setCheckoutPlanKey(planKey);
                        }
                      }} style={{
                        width: '100%', marginTop: '16px', padding: '12px', fontFamily: 'var(--sans)', fontSize: '14px', fontWeight: 600,
                        background: plan.key === 'pro' ? '#C9A84C' : '#fff', color: plan.key === 'pro' ? '#fff' : '#C9A84C',
                        border: plan.key === 'pro' ? 'none' : '1.5px solid #C9A84C', borderRadius: '10px', cursor: 'pointer',
                        transition: 'all .15s', boxShadow: plan.key === 'pro' ? '0 2px 8px rgba(201,168,76,0.2)' : 'none',
                      }}>
                        {limits?.plan === 'pro' && plan.key === 'launch' ? 'Switch to Launch' : limits?.plan === 'launch' && plan.key === 'pro' ? 'Upgrade to Pro' : `Get ${plan.name}`}
                      </button>
                    )}
                    {isCurrent && (
                      <p style={{ textAlign: 'center', marginTop: '16px', fontFamily: 'var(--sans)', fontSize: '12px', color: '#5ba89d', fontWeight: 500 }}>You're on this plan</p>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Usage summary */}
            <div style={{ background: '#faf8f5', borderRadius: '10px', padding: '16px', marginBottom: '20px' }}>
              <p style={{ fontFamily: 'var(--sans)', fontSize: '13px', fontWeight: 600, color: '#2a2725', marginBottom: '10px' }}>Your usage this month</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                <div>
                  <p style={{ fontFamily: 'var(--sans)', fontSize: '20px', fontWeight: 700, color: '#2a2725' }}>{limits?.sites?.used || 0}</p>
                  <p style={{ fontFamily: 'var(--sans)', fontSize: '11px', color: '#8c8782' }}>of {limits?.sites?.limit || 1} sites</p>
                </div>
                <div>
                  <p style={{ fontFamily: 'var(--sans)', fontSize: '20px', fontWeight: 700, color: '#2a2725' }}>{limits?.features?.unlimited_edits ? '∞' : (limits?.edits?.used || 0)}</p>
                  <p style={{ fontFamily: 'var(--sans)', fontSize: '11px', color: '#8c8782' }}>{limits?.features?.unlimited_edits ? 'unlimited edits' : `of ${limits?.edits?.limit || 30} edits`}</p>
                </div>
                <div>
                  <p style={{ fontFamily: 'var(--sans)', fontSize: '20px', fontWeight: 700, color: '#2a2725' }}>{user?.email?.split('@')[0]}</p>
                  <p style={{ fontFamily: 'var(--sans)', fontSize: '11px', color: '#8c8782' }}>{user?.email}</p>
                </div>
              </div>
            </div>

            <button onClick={() => { setShowBillingModal(false); navigate('/settings'); }} style={{ width: '100%', padding: '12px', fontFamily: 'var(--sans)', fontSize: '13px', color: '#8c8782', background: 'none', border: '1px solid #e8e3dc', borderRadius: '10px', cursor: 'pointer' }}>
              Account Settings
            </button>
          </div>
        </div>
      )}

      {/* Pricing Modal */}
      {showPricing && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }} onClick={() => !checkingOut && setShowPricing(false)}>
          <div onClick={e => e.stopPropagation()} data-testid="pricing-modal" style={{
            background: '#fff', border: '1px solid #e8e3dc', maxWidth: '700px', width: '95%',
            padding: '40px', boxShadow: '0 40px 100px rgba(0,0,0,.6)',
          }}>
            <h2 style={{ fontFamily: 'var(--sans)', fontSize: '28px', marginBottom: '4px' }}>Choose your plan</h2>
            <p style={{ color: '#8c8782', fontSize: '14px', marginBottom: '24px' }}>Your first draft is free. Subscribe to refine, customize, and publish.</p>

            {/* Toggle */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '32px' }}>
              <span style={{ fontFamily: 'var(--sans)', fontSize: '10px', letterSpacing: '.1em', textTransform: 'uppercase', color: !yearly ? '#2a2725' : '#8c8782' }}>Monthly</span>
              <button onClick={() => setYearly(!yearly)} style={{
                width: '44px', height: '24px', borderRadius: '12px', border: 'none', cursor: 'pointer',
                background: yearly ? '#C9A84C' : 'rgba(242,239,233,0.15)', position: 'relative',
              }}>
                <div style={{
                  width: '18px', height: '18px', borderRadius: '50%', background: yearly ? '#000' : '#2a2725',
                  position: 'absolute', top: '3px', transition: 'left .3s',
                  left: yearly ? '23px' : '3px',
                }} />
              </button>
              <span style={{ fontFamily: 'var(--sans)', fontSize: '10px', letterSpacing: '.1em', textTransform: 'uppercase', color: yearly ? '#2a2725' : '#8c8782' }}>Yearly</span>
              {yearly && <span style={{ fontFamily: 'var(--sans)', fontSize: '9px', color: '#C9A84C' }}>2 MONTHS FREE</span>}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              {/* Launch */}
              <div style={{ padding: '28px', border: '1px solid #e8e3dc', display: 'flex', flexDirection: 'column' }}>
                <p style={{ fontFamily: 'var(--sans)', fontSize: '10px', letterSpacing: '.15em', textTransform: 'uppercase', color: '#8c8782', marginBottom: '16px' }}>Launch</p>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px', marginBottom: '4px' }}>
                  <span style={{ fontFamily: 'var(--sans)', fontSize: '40px' }}>${yearly ? '16' : '19'}</span>
                  <span style={{ color: '#8c8782', fontSize: '13px' }}>/mo</span>
                </div>
                {yearly && <p style={{ fontFamily: 'var(--sans)', fontSize: '9px', color: '#C9A84C', marginBottom: '8px' }}>$192/yr — save $36</p>}
                <p style={{ color: '#8c8782', fontSize: '13px', marginBottom: '20px' }}>Everything you need to go live.</p>
                <div style={{ marginBottom: '24px', flex: 1 }}>
                  {['1 website', '30 Iris edits/month', 'Free subdomain', 'Contact forms + lead inbox', 'Gallery embeds', 'Site analytics'].map((f, i) => (
                    <p key={i} style={{ fontSize: '12px', color: '#8c8782', marginBottom: '6px', paddingLeft: '12px', position: 'relative' }}>
                      <span style={{ position: 'absolute', left: 0, color: '#C9A84C' }}>—</span>{f}
                    </p>
                  ))}
                </div>
                <button
                  onClick={() => handleCheckout(yearly ? 'launch_yearly' : 'launch_monthly')}
                  disabled={checkingOut}
                  className="btn-outline"
                  style={{ width: '100%', textAlign: 'center' }}
                  data-testid="checkout-launch"
                >
                  {checkingOut ? 'Redirecting...' : 'Get Started'}
                </button>
              </div>

              {/* Pro */}
              <div style={{ padding: '28px', border: '1px solid #C9A84C', background: 'rgba(196,162,101,0.03)', display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
                  <p style={{ fontFamily: 'var(--sans)', fontSize: '10px', letterSpacing: '.15em', textTransform: 'uppercase', color: '#C9A84C' }}>Pro</p>
                  <span style={{ fontFamily: 'var(--sans)', fontSize: '8px', letterSpacing: '.12em', textTransform: 'uppercase', color: '#C9A84C', background: 'rgba(196,162,101,0.08)', padding: '3px 8px' }}>Popular</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px', marginBottom: '4px' }}>
                  <span style={{ fontFamily: 'var(--sans)', fontSize: '40px' }}>${yearly ? '33' : '39'}</span>
                  <span style={{ color: '#8c8782', fontSize: '13px' }}>/mo</span>
                </div>
                {yearly && <p style={{ fontFamily: 'var(--sans)', fontSize: '9px', color: '#C9A84C', marginBottom: '8px' }}>$396/yr — save $72</p>}
                <p style={{ color: '#8c8782', fontSize: '13px', marginBottom: '20px' }}>Zero limits for serious creatives.</p>
                <div style={{ marginBottom: '24px', flex: 1 }}>
                  {['Up to 3 websites', 'Unlimited Iris edits', 'Multi-page sites', 'Custom domain', 'Blog with AI writing', 'No GoldenHour branding'].map((f, i) => (
                    <p key={i} style={{ fontSize: '12px', color: '#8c8782', marginBottom: '6px', paddingLeft: '12px', position: 'relative' }}>
                      <span style={{ position: 'absolute', left: 0, color: '#C9A84C' }}>—</span>{f}
                    </p>
                  ))}
                </div>
                <button
                  onClick={() => handleCheckout(yearly ? 'pro_yearly' : 'pro_monthly')}
                  disabled={checkingOut}
                  className="btn-primary"
                  style={{ width: '100%', textAlign: 'center' }}
                  data-testid="checkout-pro"
                >
                  {checkingOut ? 'Redirecting...' : 'Go Pro'}
                </button>
              </div>
            </div>

            <button onClick={() => setShowPricing(false)} style={{ display: 'block', margin: '12px auto 0', fontFamily: 'var(--sans)', fontSize: '10px', color: '#8c8782', background: 'none', border: 'none', cursor: 'pointer' }}>
              Maybe later
            </button>
            {/* FIX #1/#11: Recurring billing disclosure */}
            <p style={{ fontFamily: 'var(--sans)', fontSize: '10px', color: '#bfb9b3', textAlign: 'center', marginTop: '12px', lineHeight: 1.5 }}>
              {yearly ? 'Billed yearly. ' : 'Billed monthly. '}Auto-renews until you cancel. Cancel anytime from Settings. Access continues to end of billing period.
            </p>
          </div>
        </div>
      )}

      {/* SEO Audit Modal */}
      {seoSiteId && (
        <SEOAudit
          siteId={seoSiteId}
          siteName={seoSiteName}
          onClose={() => setSeoSiteId(null)}
          onSiteUpdated={fetchSites}
        />
      )}

    </div>
  );
};

export default Dashboard;
